import { supabase } from '../lib/supabase';
import { Review, ReviewFormData, UserDetails } from '../types/review';
import { ImageService } from './imageService';
import { trackReviewSubmission, trackImageUpload } from '../utils/analytics';

export class ReviewService {
  static formatTimeInProperty(years: number, months: number): string {
    const parts = [];
    if (years > 0) {
      parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
    }
    if (months > 0) {
      parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
    }
    return parts.length > 0 ? parts.join(' and ') : '0 months';
  }

  static async createReview(
    address: string,
    udprn: string | null,
    userDetails: UserDetails,
    reviewData: ReviewFormData
  ): Promise<Review> {
    try {
      // Upload images if any
      let imageUrls: string[] = [];
      if (reviewData.images && reviewData.images.length > 0) {
        // Track image upload
        const totalSizeMB = reviewData.images.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
        trackImageUpload(reviewData.images.length, totalSizeMB);
        
        imageUrls = await ImageService.uploadMultipleImages(reviewData.images);
      }

      // Determine if we should include landlord info based on property ownership
      const shouldIncludeLandlordInfo = reviewData.propertyOwnership === 'private-individual';

      // Prepare review data for database
      const reviewPayload = {
        address,
        udprn: udprn || null, // Keep as string since reviews.udprn is text type
        property_review: reviewData.property_review || null,
        agent_review: reviewData.hasLettingAgent ? (reviewData.agent_review || null) : null,
        location_review: reviewData.location_review || null,
        landlord_review: reviewData.landlord_review || null,
        property_rating: Number(reviewData.propertyRating),
        agent_rating: reviewData.hasLettingAgent ? Number(reviewData.agentRating) : null,
        location_rating: Number(reviewData.locationRating),
        landlord_rating: reviewData.landlordRating ? Number(reviewData.landlordRating) : null,
        time_in_property: this.formatTimeInProperty(
          reviewData.timeInPropertyYears,
          reviewData.timeInPropertyMonths
        ),
        date_in_property: reviewData.dateInProperty,
        landlord_name: shouldIncludeLandlordInfo ? reviewData.landlordName || null : null,
        landlord_email: shouldIncludeLandlordInfo ? reviewData.landlordEmail || null : null,
        landlord_phone: shouldIncludeLandlordInfo ? reviewData.landlordPhone || null : null,
        images: imageUrls,
        reviewer_name: userDetails.name,
        reviewer_email: userDetails.email,
        reviewer_phone: userDetails.phone || '',
        is_anonymous: userDetails.isAnonymous,
        is_verified: null, // Reviews start as null (pending review)
        // New fields for enhanced property and agent data
        property_owner: reviewData.propertyOwnership || null,
        council_housing_id: reviewData.councilHousingRegistrationNumber || null,
        council_housing_name: reviewData.councilHousingName || null,
        private_company_name: reviewData.privateCompanyName || null,
        private_company_id: reviewData.privateCompanyId || null,
        private_company_latitude: reviewData.privateCompanyLatitude || null,
        private_company_longitude: reviewData.privateCompanyLongitude || null,
        private_company_address: reviewData.privateCompanyAddress || null,
        private_company_website: reviewData.privateCompanyWebsite || null,
        private_company_phone: reviewData.privateCompanyPhone || null,
        private_company_open_hours: reviewData.privateCompanyOpenHours || null,
        student_accommodation_name: reviewData.studentAccommodationName || null,
        student_accommodation_id: reviewData.studentAccommodationId || null,
        student_accommodation_latitude: reviewData.studentAccommodationLatitude || null,
        student_accommodation_longitude: reviewData.studentAccommodationLongitude || null,
        student_accommodation_address: reviewData.studentAccommodationAddress || null,
        student_accommodation_website: reviewData.studentAccommodationWebsite || null,
        student_accommodation_phone: reviewData.studentAccommodationPhone || null,
        student_accommodation_open_hours: reviewData.studentAccommodationOpenHours || null,
        letting_agent_name: reviewData.lettingAgentName || null,
        letting_agent_place_id: reviewData.lettingAgentPlaceId || null,
        letting_agent_latitude: reviewData.lettingAgentLatitude || null,
        letting_agent_longitude: reviewData.lettingAgentLongitude || null,
        letting_agent_address: reviewData.lettingAgentAddress || null,
        letting_agent_website: reviewData.lettingAgentWebsite || null,
        letting_agent_phone: reviewData.lettingAgentPhone || null,
        letting_agent_open_hours: reviewData.lettingAgentOpenHours || null,
        experienced_issues: reviewData.experiencedIssues || null,
        consent_compensation_contact: reviewData.consentCompensationContact || false,
      };

      const { data, error } = await supabase
        .from('reviews')
        .insert([reviewPayload])
        .select()
        .single();

      if (error) {
        console.error('Error creating review:', error);
        throw error;
      }

      // Track successful review submission
      trackReviewSubmission({
        property_address: address,
        property_rating: reviewData.propertyRating,
        has_images: imageUrls.length > 0,
        review_type: userDetails.isAnonymous ? 'anonymous' : 'named',
        property_owner_type: reviewData.propertyOwnership || undefined,
        has_letting_agent: reviewData.hasLettingAgent || false
      });

      return data as Review;
    } catch (error) {
      console.error('Error in createReview:', error);
      throw error;
    }
  }

  static async getReviewsByAddress(address: string): Promise<Review[]> {
    try {
      const { data, error } = await supabase
        .from('public_reviews')
        .select('*')
        .eq('address', address)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getReviewsByAddress:', error);
      return [];
    }
  }

  static async getReviewsByUdprn(udprn: string): Promise<Review[]> {
    try {
      const { data, error } = await supabase
        .from('public_reviews')
        .select('*')
        .eq('udprn', udprn)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews by UDPRN:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getReviewsByUdprn:', error);
      return [];
    }
  }

  static async getAllReviews(limit: number = 50): Promise<Review[]> {
    try {
      const { data, error } = await supabase
        .from('public_reviews')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching all reviews:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllReviews:', error);
      return [];
    }
  }
}

export class OtpService {
  static async requestOtp(phone: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('smsotp')
        .upsert({ phone: phone }, { onConflict: 'phone' });

      if (error) {
        console.error('Error requesting OTP:', error);
        throw new Error('Failed to request OTP');
      }
    } catch (error) {
      console.error('Error in requestOtp:', error);
      throw error;
    }
  }

  static async verifyOtp(phone: string, otp: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('smsotp')
        .select('created_at')
        .eq('phone', phone)
        .eq('otp', otp)
        .single();

      if (error || !data) {
        console.error('OTP verification failed:', error);
        return false;
      }

      // Delete the OTP record to prevent reuse
      const { error: deleteError } = await supabase
        .from('smsotp')
        .delete()
        .eq('phone', phone);

      if (deleteError) {
        console.error('Error deleting OTP record:', deleteError);
        // Don't fail verification if deletion fails
      }

      return true;
    } catch (error) {
      console.error('Error in verifyOtp:', error);
      return false;
    }
  }
}